import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Clássico" template — the original Orçamigo layout.
 * Full-width colored header + rounded client box + grid table + rounded total.
 * Default template. Only one available for free users.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const { primaryColor, grayVeryLight, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === 1. HEADER ===
  const headerHeight = 48;
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  const documentTitle = resolveDocumentTitle(data);
  const hasCompanyData =
    data.company &&
    (data.company.name ||
      data.company.cnpj ||
      data.company.address ||
      data.company.phone ||
      data.company.email ||
      data.company.logoUrl);

  doc.setTextColor(...white);
  let yPos = 12;

  if (!hasCompanyData) {
    doc.setFont('helvetica', 'bold');
    // Sem dados da empresa o título tem a faixa inteira; com eles, a caixa da
    // logo (28mm no canto direito) é o que limita.
    const cabido = fitHeaderTitle(doc, documentTitle, pageWidth - margin * 2, 26);
    doc.setFontSize(cabido.size);
    doc.text(cabido.text, pageWidth / 2, headerHeight / 2 + 4, { align: 'center' });
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
    renderDocumentSubtitle(doc, data, {
      x: pageWidth / 2, y: headerHeight / 2 + 4, maxWidth: pageWidth - margin * 2,
      color: white, align: 'center',
    });
  } else {
    doc.setFont('helvetica', 'bold');
    const cabido = fitHeaderTitle(doc, documentTitle, pageWidth - margin * 2 - 31, 22);
    doc.setFontSize(cabido.size);
    doc.text(cabido.text, margin, yPos);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
    yPos += renderDocumentSubtitle(doc, data, {
      x: margin, y: yPos, maxWidth: pageWidth - margin * 2 - 31, color: white,
    });

    yPos += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(data.company!.name || 'Minha Empresa', margin, yPos);

    if (data.company!.cnpj) {
      yPos += 5;
      doc.setFontSize(9);
      doc.text(`CNPJ: ${data.company!.cnpj}`, margin, yPos);
    }

    if (data.company!.address) {
      yPos += 4;
      doc.setFontSize(9);
      const addressText = doc.splitTextToSize(
        `Endereço: ${data.company!.address}`,
        pageWidth / 2 - margin,
      )[0];
      doc.text(addressText, margin, yPos);
    }

    const contactParts: string[] = [];
    if (data.company!.phone) contactParts.push(`Tel: ${data.company!.phone}`);
    if (data.company!.email) contactParts.push(`E-mail: ${data.company!.email}`);
    if (contactParts.length > 0) {
      yPos += 4;
      doc.setFontSize(9);
      doc.text(contactParts.join('  |  '), margin, yPos);
    }

    const logoBoxSize = 28;
    const logoBoxX = pageWidth - margin - logoBoxSize;
    const logoBoxY = 7;

    if (logoBase64) {
      try {
        const logoFormat = getJsPdfImageFormat(logoBase64);
        doc.addImage(logoBase64, logoFormat, logoBoxX, logoBoxY, logoBoxSize, logoBoxSize);
      } catch {
        drawLogoPlaceholder(doc, logoBoxX, logoBoxY, logoBoxSize, white, grayText);
      }
    } else {
      drawLogoPlaceholder(doc, logoBoxX, logoBoxY, logoBoxSize, white, grayText);
    }
  }

  // === 2. CLIENT BLOCK ===
  yPos = headerHeight + 8;
  const rowHeight = 10;
  const contentWidth = pageWidth - margin * 2;

  interface ClientRow {
    label: string;
    value: string;
    rightLabel?: string;
    rightValue?: string;
  }

  const clientRows: ClientRow[] = [];
  const formattedDate = formatQuoteDate(data.quoteDate);

  clientRows.push({
    label: 'Cliente:',
    value: data.client.name || 'Não informado',
    rightLabel: 'Data de emissão:',
    rightValue: formattedDate,
  });
  if (data.client.phone) {
    clientRows.push({ label: 'Telefone:', value: data.client.phone });
  }
  if (data.client.document) {
    clientRows.push({
      label: detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ:' : 'CPF:',
      value: formatDocument(data.client.document),
    });
  }
  if (data.client.address) {
    const addressText = doc.splitTextToSize(data.client.address, contentWidth - 30)[0];
    clientRows.push({ label: 'Endereço:', value: addressText });
  }
  if (data.validityDays && data.quoteDate) {
    clientRows.push({
      label: 'Validade:',
      value: calcValidityDate(data.quoteDate, data.validityDays),
    });
  }
  // Referência EXTERNA do cliente (ordem de compra, contrato, chamado) — perto
  // do número do documento, mas NÃO é o `sequenceId` do Orçamigo.
  if (data.reference) {
    clientRows.push({ label: 'Referência:', value: data.reference });
  }

  const totalBlockHeight = clientRows.length * rowHeight;
  doc.setFillColor(...grayVeryLight);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, yPos, contentWidth, totalBlockHeight, 2, 2, 'FD');

  for (let i = 1; i < clientRows.length; i++) {
    const lineY = yPos + i * rowHeight;
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.3);
    doc.line(margin, lineY, margin + contentWidth, lineY);
  }

  let textY = yPos;
  clientRows.forEach((row) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    doc.text(row.label, margin + 3, textY + 6);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(row.value, margin + 24, textY + 6);

    if (row.rightLabel && row.rightValue) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayText);
      doc.text(
        `${row.rightLabel} ${row.rightValue}`,
        pageWidth - margin - 3,
        textY + 6,
        { align: 'right' },
      );
    }
    textY += rowHeight;
  });
  yPos += totalBlockHeight;

  // === 3. ITEMS TABLE ===
  yPos += 8;
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);

  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 16, halign: 'center' as const },
        2: { cellWidth: 14, halign: 'center' as const },
        3: { cellWidth: 28, halign: 'right' as const },
        4: { cellWidth: 22, halign: 'center' as const },
        5: { cellWidth: 30, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 18, halign: 'center' as const },
        2: { cellWidth: 14, halign: 'center' as const },
        3: { cellWidth: 32, halign: 'right' as const },
        4: { cellWidth: 32, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    headStyles: {
      fillColor: primaryColor,
      textColor: white,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      cellPadding: 4,
    },
    columnStyles,
    bodyStyles: { fontSize: 8, cellPadding: 3 },
    alternateRowStyles: { fillColor: grayVeryLight },
    styles: { lineColor: [200, 200, 200], lineWidth: 0.3 },
    theme: 'grid',
    margin: { left: margin, right: margin },
  });

  // === 4. TOTAL ===
  const totals = pdfQuoteTotals(data);
  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 4;

  const totalBoxWidth = 78;
  const totalBoxHeight = 14;
  const totalBoxX = pageWidth - margin - totalBoxWidth;

  // Subtotal/descontos/frete acima do box. Sem nada a decompor devolve o yPos
  // intacto e o PDF sai igual ao histórico (armadilha #13: yPos é manual).
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: totalBoxWidth,
  });

  doc.setFillColor(...primaryColor);
  doc.roundedRect(totalBoxX, yPos, totalBoxWidth, totalBoxHeight, 1, 1, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('TOTAL GERAL:', totalBoxX + 4, yPos + 9);

  doc.setFontSize(12);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 4, yPos + 9, { align: 'right' });

  yPos += totalBoxHeight + 6;

  // === 5. OBSERVATIONS ===
  const hasObservations = data.warranty || data.notes || data.validityDays;
  if (hasObservations) {
    let estimatedLines = 2;
    if (data.validityDays) estimatedLines += 1;
    if (data.warranty) estimatedLines += Math.ceil(data.warranty.length / 80) + 1;
    if (data.notes) estimatedLines += Math.ceil(data.notes.length / 80) + 1;

    const obsBoxHeight = Math.max(35, estimatedLines * 5 + 10);

    doc.setFillColor(...grayLight);
    doc.roundedRect(margin, yPos, contentWidth, obsBoxHeight, 2, 2, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Observações', margin + 4, yPos + 7);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);

    let termY = yPos + 14;
    let termNumber = 1;

    if (data.validityDays && data.quoteDate) {
      const validityFormatted = calcValidityDate(data.quoteDate, data.validityDays);
      doc.text(
        `${termNumber}. Validade: Este orçamento é válido até ${validityFormatted}.`,
        margin + 4,
        termY,
      );
      termY += 5;
      termNumber++;
    }
    if (data.warranty) {
      const warrantyText = doc.splitTextToSize(
        `${termNumber}. Garantia: ${data.warranty}`,
        contentWidth - 10,
      );
      doc.text(warrantyText, margin + 4, termY);
      termY += 4 * warrantyText.length + 1;
      termNumber++;
    }
    if (data.notes) {
      const notesText = doc.splitTextToSize(
        `${termNumber}. Observações: ${data.notes}`,
        contentWidth - 10,
      );
      doc.text(notesText, margin + 4, termY);
      termY += 4 * notesText.length + 1;
      termNumber++;
    }
    yPos += obsBoxHeight + 6;
  }

  // === 5.5 PAGAMENTO ===
  // Formas aceitas + condições de pagamento. O retorno avança o `yPos`:
  // tudo abaixo (PIX, fotos, assinatura) é posicionado por coordenada.
  // === DIAGNÓSTICO TÉCNICO (Ordem de Serviço) ===
  // Só desenha quando a OS tem os campos preenchidos; devolve o yPos avançado
  // (jsPDF não tem layout — armadilha #13).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // === 5.6 PIX ===
  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 6. TERMS ===
  if (data.termsConditions) {
    const termsLines = doc.splitTextToSize(data.termsConditions, contentWidth - 10);
    const termsBoxHeight = Math.max(20, termsLines.length * 4 + 14);

    doc.setFillColor(245, 245, 245);
    doc.roundedRect(margin, yPos, contentWidth, termsBoxHeight, 2, 2, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Termos e condições', margin + 4, yPos + 6);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(termsLines, margin + 4, yPos + 12);

    yPos += termsBoxHeight + 8;
  }

  // === 6.5 PHOTOS ===
  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 7. SIGNATURE ===
  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  // === 8. FOOTER ===
  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}

function drawLogoPlaceholder(
  doc: jsPDF,
  x: number,
  y: number,
  size: number,
  bg: [number, number, number],
  fg: [number, number, number],
): void {
  doc.setFillColor(...bg);
  doc.roundedRect(x, y, size, size, 3, 3, 'F');
  doc.setTextColor(...fg);
  doc.setFontSize(7);
  doc.text('LOGO', x + size / 2, y + size / 2 + 2, { align: 'center' });
}
